import { ErrorMessage, PageContainer, Spinner } from "@/components";
import SearchInput from "@/components/Search";
import { useAllCompaniesQuery } from "@/hooks/api/internship.queries";
import { Building2, Globe, MapPin, ChevronRight, Briefcase, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function DiscoverCompanies() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, refetch } = useAllCompaniesQuery({ page: 1, limit: 50 });

  const allCompanies = data?.companies || [];
  const searchLower = search.toLowerCase().trim();
  const companies = searchLower
    ? allCompanies.filter((c: any) =>
      (c.name || "").toLowerCase().includes(searchLower) ||
      (c.industry || "").toLowerCase().includes(searchLower)
    )
    : allCompanies;

  if (isError) {
    return <ErrorMessage message="Failed to load partnered companies." onRetry={() => refetch()} />;
  }

  // Generate a deterministic accent color from company name
  const accentColors = [
    "from-indigo-500 to-violet-600",
    "from-rose-500 to-pink-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-sky-500 to-blue-600",
    "from-purple-500 to-fuchsia-600",
  ];
  const getAccent = (name = "") => accentColors[name.charCodeAt(0) % accentColors.length];

  return (
    <PageContainer className="min-h-screen">
      {/* Hero Header */}
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-700 p-8 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)", backgroundSize: "50px 50px" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-yellow-300" />
            <span className="text-sm font-semibold text-purple-200 uppercase tracking-widest">Recruiter Hub</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-2">Recruiter Partners</h1>
          <p className="text-purple-200 text-base max-w-xl">
            Explore top companies actively recruiting students from our campus.
          </p>
          <div className="mt-5 flex items-center gap-3 text-sm text-purple-200">
            <span className="flex items-center gap-1.5"><Building2 size={14} />{isLoading ? "..." : allCompanies.length} Partners</span>
            <span className="w-1 h-1 bg-purple-400 rounded-full" />
            <span className="flex items-center gap-1.5"><Briefcase size={14} />Exclusive Openings</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 max-w-lg">
        <SearchInput
          placeholder="Search companies or industries..."
          value={search}
          onValueChange={setSearch}
          className={{
            inputGroupClass: "rounded-xl border border-gray-200 bg-white shadow-sm focus-within:border-violet-400 focus-within:ring-4 focus-within:ring-violet-100 h-11 transition-all",
            inputClass: "focus-visible:ring-0",
          }}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Spinner />
          <p className="text-gray-400 text-sm animate-pulse">Loading recruiter partners...</p>
        </div>
      ) : companies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-center bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="w-20 h-20 bg-violet-50 rounded-full flex items-center justify-center mb-5">
            <Building2 size={32} className="text-violet-300" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No companies found</h3>
          <p className="text-gray-400 text-sm">Try a different search or check back later.</p>
          {search && (
            <button onClick={() => setSearch("")} className="mt-4 text-violet-600 text-sm font-semibold hover:underline">
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {companies.map((company: any) => {
            const cId = company.id || company._id;
            const name = company.name || "Unknown Company";
            const accent = getAccent(name);
            return (
              <div
                key={cId}
                onClick={() => navigate(`/discover/companies/${cId}`)}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-violet-200 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col"
              >
                {/* Gradient top stripe + logo area */}
                <div className={`h-20 bg-gradient-to-br ${accent} relative flex-shrink-0`}>
                  <div className="absolute bottom-0 left-6 translate-y-1/2">
                    <div className="w-14 h-14 bg-white rounded-xl border-2 border-white shadow-md flex items-center justify-center overflow-hidden">
                      {company.logo_url ? (
                        <img src={company.logo_url} alt={name} className="w-full h-full object-contain p-1.5" />
                      ) : (
                        <span className={`text-xl font-black bg-gradient-to-br ${accent} bg-clip-text text-transparent`}>
                          {name.charAt(0)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-10 pb-5 px-6 flex flex-col flex-1">
                  <h3 className="text-lg font-extrabold text-gray-900 group-hover:text-violet-700 transition-colors mb-1 line-clamp-1">
                    {name}
                  </h3>

                  {company.industry && (
                    <span className="inline-block self-start px-2.5 py-1 bg-violet-50 text-violet-700 text-[11px] font-bold uppercase tracking-wider rounded-full mb-3 border border-violet-100">
                      {company.industry}
                    </span>
                  )}

                  <div className="space-y-2 text-sm text-gray-500 mb-5 flex-1">
                    {company.headquarters && (
                      <div className="flex items-center gap-2">
                        <MapPin size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">{company.headquarters}</span>
                      </div>
                    )}
                    {company.website && (
                      <div className="flex items-center gap-2">
                        <Globe size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate text-xs">{(company.website || "").replace(/^https?:\/\//, "")}</span>
                      </div>
                    )}
                    {company.description && (
                      <p className="text-xs text-gray-400 line-clamp-2 mt-2 leading-relaxed">{company.description.replace(/<[^>]+>/g, "")}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400 font-medium">View openings</span>
                    <span className={`flex items-center gap-1 text-sm font-bold bg-gradient-to-r ${accent} bg-clip-text text-transparent group-hover:gap-2 transition-all`}>
                      Explore <ChevronRight size={15} className="text-violet-600" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
